import React from "react";
import { motion } from "framer-motion";
import { PageHero, Overline, PrimaryButton } from "@/components/common";
import { Reveal, staggerParent, staggerChild } from "@/lib/motion";
import { IMAGES, TEAM_STATS, COMPANY } from "@/data/site";

export default function Team() {
  return (
    <>
      <PageHero
        overline="Our Team"
        title="A team built on trust and experience"
        intro="Hand-picked, highly skilled and trusted — many have been with us for decades. We think our staff retention says everything about who we are."
        image={IMAGES.hayesInt2}
      />

      {/* Stats */}
      <section className="container-x py-20 md:py-28">
        <motion.div
          variants={staggerParent}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-10%" }}
          className="grid gap-px border-l border-t border-slate-200 sm:grid-cols-3"
        >
          {TEAM_STATS.map((s) => (
            <motion.div key={s.label} variants={staggerChild} className="border-b border-r border-slate-200 p-8 md:p-10">
              <p className="font-display text-5xl font-bold text-[#0A1128] md:text-6xl">{s.value}</p>
              <p className="mt-3 text-sm uppercase tracking-widest text-slate-500">{s.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Narrative + image split */}
      <section className="border-t border-slate-200">
        <div className="grid lg:grid-cols-2">
          <div className="container-x flex items-center py-20 md:py-28 lg:pr-16">
            <div>
              <Overline>Based at Alphabet House, Milton Keynes</Overline>
              <Reveal>
                <p className="mt-6 max-w-xl text-balance font-display text-2xl font-medium leading-tight tracking-tight text-[#0A1128] md:text-3xl">
                  Our team is hand-picked, highly skilled and trusted — with significant experience across the niches we serve.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 space-y-5 text-base leading-relaxed text-slate-600">
                  <p>
                    We run a 10-strong employed team alongside a trusted network of around 10 specialist
                    subcontractors/companies — all working out of our own premises at Alphabet House, Bleak Hall.
                  </p>
                  <p>
                    Most of our team have been with us for years — many for decades. Our Site Manager,{" "}
                    <span className="font-semibold text-[#0A1128]">John Paice</span>, has been with the business for
                    over 35 years. Together, we've delivered hundreds of successful projects — most of them from
                    long-standing relationships and referrals.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
          <div className="relative min-h-[50vh] overflow-hidden lg:min-h-full">
            <img src={IMAGES.hayesOffice} alt="Completed office fit-out by the Andrews Building Company team" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Retention highlight */}
      <section className="relative overflow-hidden bg-[#0A1128] py-24 md:py-32">
        <div className="noise" />
        <div className="container-x relative">
          <Overline dark>Exceptional staff retention</Overline>
          <p className="mt-8 max-w-4xl text-balance font-display text-3xl font-medium uppercase leading-tight tracking-tight text-white md:text-5xl">
            Good people, good work, every time. That's the Andrews Building Company way.
          </p>
          <div className="mt-12">
            <PrimaryButton to="/contact" testId="team-contact-btn" light>
              Work With Us
            </PrimaryButton>
          </div>
        </div>
      </section>
    </>
  );
}
